import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import { Link, useLocation } from 'react-router';

import { Tag } from '@/components/Tag';

import { useNavigationContext } from '../../context/NavigationContext';
import resolveFullPath from '../../utils/resolveFullPath';

import type { SxProps } from '@mui/material';

// Helper to normalize openPaths level value to a string array (DRY)
const normalizePathsAtLevel = (value: unknown): string[] => {
  const raw = value as string | string[] | undefined;
  if (Array.isArray(raw)) return raw;
  if (raw === '') return [''];
  return raw ? [raw] : [];
};

export interface NavigationItemData {
  key: string;
  id?: string;
  name?: string;
  icon?: ReactNode;
  path?: string;
  children?: NavigationItemData[];
  tag?: string | null;
  alignIconWithTopLevel?: boolean;
  textSx?: SxProps;
}

interface NavigationItemProps {
  item: NavigationItemData;
  level?: number;
  parentPath?: string;
}

const NavigationItem = ({ item, parentPath = '', level = 0 }: NavigationItemProps) => {
  const location = useLocation();
  const { openPaths, setOpenPaths, isMenuOpen, setOpenMenu } = useNavigationContext();
  const [forceOpen, setForceOpen] = useState(true);
  const hasChildren = !!item.children;
  const fullPath = useMemo(() => resolveFullPath(parentPath, item.path), [parentPath, item.path]);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setForceOpen(isMenuOpen);
  }, [isMenuOpen]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const isAnyDescendantActive = useMemo(() => {
    const check = (node: NavigationItemData, basePath: string): boolean => {
      const nodePath = resolveFullPath(basePath, node.path);
      const hasOwnPath = Boolean(node.path);
      if (
        hasOwnPath &&
        (location.pathname === nodePath || location.pathname.startsWith(`${nodePath}/`))
      )
        return true;
      return node.children?.some(child => check(child, nodePath)) ?? false;
    };
    return check(item, parentPath);
  }, [item, parentPath, location.pathname]);

  const shouldBeOpen = useMemo(() => {
    const pathsAtLevel = normalizePathsAtLevel(openPaths[level]);
    if (item.path === '/markets') {
      return forceOpen;
    }

    if (isAnyDescendantActive) {
      if (isMenuOpen) {
        if (level >= 2) return false;
        if (level === 1) {
          return pathsAtLevel.includes(fullPath) || (!item.path && pathsAtLevel.includes(''));
        }
        if (level === 0) {
          return pathsAtLevel.includes(fullPath) || (!item.path && pathsAtLevel.includes(''));
        }
      }
      return true;
    }

    const isExplicitlyOpen =
      pathsAtLevel.includes(fullPath) || (!item.path && pathsAtLevel.includes(''));
    if (level === 0 && pathsAtLevel.includes('')) return true;
    return isExplicitlyOpen;
  }, [openPaths, level, item.path, isAnyDescendantActive, fullPath, forceOpen, isMenuOpen]);

  const handleClick = useCallback(() => {
    if (level === 0) {
      if (!item.path) setOpenMenu(true);

      if (item.path === '/markets') {
        setForceOpen(prev => !prev);
        return;
      }
    }

    setOpenPaths(prev => {
      const currentAtLevel = normalizePathsAtLevel(prev[level]);
      const isOpen = currentAtLevel.includes(fullPath);

      const updated: { [key: number]: string[] } = { ...prev };

      // Level 0 items should always open when menu is closed, but can toggle when menu is open
      if (level === 0) {
        if (!isMenuOpen) {
          // When menu is closed, always open level 0 items
          updated[0] = fullPath ? Array.from(new Set([fullPath])) : [];
          const rawLevel0 = updated[0] as unknown as string | string[] | undefined;
          let at0: string[];
          if (Array.isArray(rawLevel0)) {
            at0 = rawLevel0;
          } else if (rawLevel0) {
            at0 = [rawLevel0];
          } else {
            at0 = [];
          }
          const withMarkets = Array.from(new Set(at0));
          if (fullPath) withMarkets.push(fullPath);
          updated[0] = Array.from(new Set(withMarkets));
          return updated;
        }
        // When menu is open, allow normal toggle behavior (fall through to normal logic)
      }

      if (isOpen) {
        const remaining = currentAtLevel.filter(p => p !== fullPath);
        if (remaining.length) updated[level] = remaining;
        else delete updated[level];

        for (const k of Object.keys(updated)) {
          const keyNum = Number(k);
          if (keyNum > level) {
            const filtered = (updated[keyNum] || []).filter(p => !p.startsWith(fullPath));
            if (filtered.length) updated[keyNum] = filtered;
            else delete updated[keyNum];
          }
        }
      } else if (level === 0 && isMenuOpen) {
        // When menu is open and level 0 is not open, open it
        updated[0] = fullPath ? Array.from(new Set([fullPath])) : [];
        const rawLevel0 = updated[0] as unknown as string | string[] | undefined;
        let at0: string[];
        if (Array.isArray(rawLevel0)) {
          at0 = rawLevel0;
        } else if (rawLevel0) {
          at0 = [rawLevel0];
        } else {
          at0 = [];
        }
        const withMarkets = Array.from(new Set(at0));
        if (fullPath) withMarkets.push(fullPath);
        updated[0] = Array.from(new Set(withMarkets));
      } else if (level === 1) {
        updated[level] = [fullPath];

        for (const k of Object.keys(updated)) {
          const keyNum = Number(k);
          if (keyNum > level) {
            const filtered = (updated[keyNum] || []).filter(p => p.startsWith(fullPath));
            if (filtered.length) updated[keyNum] = filtered;
            else delete updated[keyNum];
          }
        }
      } else {
        updated[level] = [...currentAtLevel, fullPath];
      }

      return updated;
    });
  }, [level, setOpenPaths, item.path, setOpenMenu, fullPath, isMenuOpen]);

  const paddingLeftNumber = level === 0 ? 24 : 37 + 12 * level + 8;
  const paddingLeft = `${paddingLeftNumber}px`;

  const isSelected = useMemo(() => {
    if (!hasChildren) {
      return location.pathname === fullPath || location.pathname.startsWith(`${fullPath}/`);
    }
    if (!isMenuOpen) {
      return location.pathname === fullPath || isAnyDescendantActive;
    }

    return location.pathname === fullPath || (isAnyDescendantActive && !shouldBeOpen);
  }, [hasChildren, isMenuOpen, location.pathname, fullPath, isAnyDescendantActive, shouldBeOpen]);

  const handleNonRootItemClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (location.pathname === fullPath) {
        e.preventDefault();
        setOpenMenu(true);
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setOpenMenu(false);
        timeoutRef.current = null;
      }, 850);
    },
    [setOpenMenu, location.pathname, fullPath]
  );

  const handleRootItemClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      setOpenMenu(true);

      timeoutRef.current = setTimeout(() => {
        setOpenMenu(false);
        timeoutRef.current = null;
      }, 850);
    },
    [setOpenMenu]
  );

  const buttonProps = useMemo(() => {
    if (!hasChildren && item.path) {
      const onClick = level === 0 ? handleRootItemClick : handleNonRootItemClick;

      return {
        component: Link,
        to: item.path,
        onClick
      };
    }
    return { onClick: handleClick };
  }, [hasChildren, item.path, handleClick, level, handleNonRootItemClick, handleRootItemClick]);

  return (
    <ListItem
      disablePadding
      component='div'
      sx={{
        display: 'block',
        ...(level === 0 && { '&:last-child': { mt: 'auto', mb: 0 } }),
        '.MuiListItemButton-root': shouldBeOpen
          ? {
              backgroundColor: '#121836'
            }
          : {}
      }}
    >
      <ListItemButton
        selected={isSelected || shouldBeOpen}
        {...buttonProps}
        sx={{
          gap: '8px',
          minHeight: level === 0 ? 56 : 40,
          pl: paddingLeft,
          pr: 2,
          justifyContent: isMenuOpen ? 'initial' : 'center',
          '&.Mui-selected': (() => {
            if (isSelected) {
              if (hasChildren) {
                return {
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'primary.main'
                };
              }
              return {
                backgroundColor: '#341616 !important'
              };
            }
            return {
              backgroundColor: 'background.paper'
            };
          })(),
          '&.Mui-selected:hover': isSelected
            ? {}
            : {
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
        }}
      >
        {item.icon && (
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: 'center',
              position: 'relative',
              right: item.alignIconWithTopLevel && level > 0 ? paddingLeftNumber - 24 : 0
            }}
          >
            {item.icon}
          </ListItemIcon>
        )}

        <ListItemText
          primary={
            <Box
              sx={{
                display: isMenuOpen ? 'flex' : 'none',
                alignItems: 'center',
                fontWeight: isSelected ? 600 : 500,
                gap: '8px',
                position: 'relative',
                right: item.alignIconWithTopLevel && level > 0 ? paddingLeftNumber - 24 : 0
              }}
            >
              <Typography
                noWrap
                sx={{
                  fontSize: level > 0 ? '14px' : '16px',
                  display: 'inline-flex',
                  overflow: 'hidden',
                  ...item.textSx
                }}
              >
                {item.name}
              </Typography>
              {item.tag && (
                <Box
                  sx={{
                    marginLeft: level === 0 ? '16px' : 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                >
                  <Tag label={item.tag} />
                </Box>
              )}

              {hasChildren &&
                isMenuOpen &&
                (shouldBeOpen ? (
                  <ExpandLess sx={{ mb: '-3px' }} />
                ) : (
                  <ExpandMore sx={{ mb: '-3px' }} />
                ))}
            </Box>
          }
        />
      </ListItemButton>

      {isMenuOpen && hasChildren && (
        <Collapse
          unmountOnExit
          in={shouldBeOpen}
          timeout='auto'
        >
          <List
            disablePadding
            component='div'
          >
            {item.children?.map(childItem => (
              <NavigationItem
                key={childItem.key}
                item={childItem}
                level={level + 1}
                parentPath={fullPath}
              />
            ))}
          </List>
        </Collapse>
      )}
    </ListItem>
  );
};

export default NavigationItem;
